import { QueryClient, QueryFunction } from '@tanstack/react-query';
import { persistentAuth } from '@/services/persistentAuth';
import { parseApiErrorResponse, readApiErrorBody } from '@/lib/errors';

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const body = await readApiErrorBody(res);
    if (body) {
      throw parseApiErrorResponse(res.status, body);
    }
    throw new Error(res.statusText || `Request failed with status ${res.status}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined
): Promise<Response> {
  try {
    const res = await fetch(url, {
      method,
      headers: data ? { 'Content-Type': 'application/json' } : {},
      body: data ? JSON.stringify(data) : undefined,
      credentials: 'include',
    });

    // CRITICAL: Intercept 401 to refresh session and retry
    // This allows requests to succeed transparently when session expires
    if (res.status === 401 && !url.includes('/api/auth/')) {
      console.log('🔄 401 detected, attempting automatic session refresh...');
      // Do not clear auth on failure here — session may still be valid on another path
      const success = await persistentAuth.refreshSession({ clearOnFailure: false, force: true });

      if (success) {
        console.log('✅ Session refreshed, retrying request...');
        const retryRes = await fetch(url, {
          method,
          headers: data ? { 'Content-Type': 'application/json' } : {},
          body: data ? JSON.stringify(data) : undefined,
          credentials: 'include',
        });

        await throwIfResNotOk(retryRes);
        return retryRes;
      }
    }

    await throwIfResNotOk(res);
    return res;
  } catch (error) {
    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error: Please check your internet connection and try again.');
    }
    throw error;
  }
}

type UnauthorizedBehavior = 'returnNull' | 'throw';

async function fetchWithAuthRetry(url: string): Promise<Response> {
  let res = await fetch(url, { credentials: 'include' });

  if (res.status === 401 && !url.includes('/api/auth/')) {
    console.log('🔄 Query 401 detected, attempting automatic session refresh...');
    const success = await persistentAuth.refreshSession({ clearOnFailure: false });
    if (success) {
      res = await fetch(url, { credentials: 'include' });
    }
  }

  return res;
}

export const getQueryFn: <T>(options: { on401: UnauthorizedBehavior }) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Build URL properly handling objects as query parameters
    let url = queryKey[0] as string;
    if (queryKey[1] && typeof queryKey[1] === 'object') {
      const params = new URLSearchParams();
      Object.entries(queryKey[1] as Record<string, string>).forEach(([key, value]) => {
        params.append(key, value);
      });
      const queryString = params.toString();
      url += queryString ? `?${queryString}` : '';
    }

    const res = await fetchWithAuthRetry(url);

    if (unauthorizedBehavior === 'returnNull' && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: 'throw' }),
      // Allow cross-device Mainstage sync: data becomes stale so focus/reconnect refetch works.
      // Individual auth queries can still override with longer staleTime.
      refetchInterval: false,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      staleTime: 30 * 1000, // 30 seconds
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
